import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProducts, getCategories } from '../api';
import ProductCard from '../components/common/ProductCard';
import { FiSearch, FiFilter, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './Products.css';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const ordering = searchParams.get('ordering') || '-created_at';
  const PAGE_SIZE = 12;

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, ordering };
    if (search) params.search = search;
    if (category) params.category = category;
    getProducts(params)
      .then(({ data }) => {
        setProducts(data.results || []);
        setCount(data.count || 0);
      })
      .finally(() => setLoading(false));
  }, [page, search, category, ordering]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { getCategories().then(({ data }) => setCategories(data.results || [])); }, []);
  useEffect(() => { setPage(1); }, [search, category, ordering]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="products-page">
      <div className="container">
        {/* Page Header */}
        <div className="products-header">
          <div>
            <h1 className="page-title">Products</h1>
            <p className="text-muted">{count} products found</p>
          </div>
          <button className="btn btn-outline btn-sm filter-toggle" onClick={() => setFilterOpen(!filterOpen)}>
            <FiFilter /> Filters {filterOpen ? <FiX /> : null}
          </button>
        </div>

        {/* Filter Bar */}
        <div className={`filter-bar ${filterOpen ? 'open' : ''}`}>
          {/* Search */}
          <div className="filter-search">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setParam('search', e.target.value)}
            />
            {search && <button onClick={() => setParam('search', '')}><FiX /></button>}
          </div>

          {/* Category Filter */}
          <select value={category} onChange={(e) => setParam('category', e.target.value)}>
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>

          {/* Sort */}
          <select value={ordering} onChange={(e) => setParam('ordering', e.target.value)}>
            <option value="-created_at">Newest First</option>
            <option value="created_at">Oldest First</option>
            <option value="price">Price: Low to High</option>
            <option value="-price">Price: High to Low</option>
          </select>

          {(search || category) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSearchParams({})}>
              <FiX /> Clear All
            </button>
          )}
        </div>

        {/* Category Tags */}
        {categories.length > 0 && (
          <div className="category-tags">
            <button
              className={`cat-tag ${!category ? 'active' : ''}`}
              onClick={() => setParam('category', '')}
            >All</button>
            {categories.map((c) => (
              <button
                key={c.id}
                className={`cat-tag ${category === c.slug ? 'active' : ''}`}
                onClick={() => setParam('category', c.slug)}
              >{c.name}</button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 340, borderRadius: 8 }} />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No products found</h3>
            <p className="text-muted">Try adjusting your search or filters</p>
            <button className="btn btn-primary" onClick={() => setSearchParams({})}>Clear Filters</button>
          </div>
        ) : (
          <div className="grid-4">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <FiChevronLeft /> Prev
            </button>
            <span className="page-info">Page {page} of {totalPages}</span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next <FiChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
